import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Bed, Users, Wifi, Instagram, Facebook, Waves, Droplets, Mail } from 'lucide-react';
import ImageCarousel from '../components/ImageCarousel';
import HeroCarousel from '../components/HeroCarousel';
import TemperatureWidget from '../components/TemperatureWidget';
import Header from '../components/Header';
import { useLanguage } from '../context/LanguageContext';

const Home = () => {
  const navigate = useNavigate();
  const { t, language, getRoutes } = useLanguage();
  const routes = getRoutes();
  
  const handleInquiry = () => {
    navigate(routes.booking);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white">
      {/* Header with Logo and Navigation */}
      <Header />

      {/* Hero Section */}
      <section 
        id="home"
        className="pt-40 md:pt-48 pb-16 px-4 relative min-h-[700px] flex items-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1537956965359-7573183d1f57?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwyfHx0aGFpbGFuZCUyMGJlYWNofGVufDB8fHx8MTc2NjEyOTc1NXww&ixlib=rb-4.1.0&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        {/* Overlay for better text readability */}
        <div className="absolute inset-0 bg-black/40"></div>
        
        {/* Temperature Widget */}
        <TemperatureWidget />
        
        <div className="container mx-auto max-w-6xl relative z-10">
          <div className="text-center space-y-6 animate-fade-in">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white leading-tight drop-shadow-lg px-4">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-400">
                {t('home.greeting')}
              </span>
            </h1>
            <p className="text-base md:text-lg text-white max-w-6xl mx-auto leading-relaxed drop-shadow-md px-4">
              {t('home.intro')}
            </p>
            
            {/* Hero Carousel - Smaller */}
            <div className="pt-6 pb-4 px-4 w-full max-w-6xl mx-auto">
              <HeroCarousel />
            </div>
            
            <p className="text-base md:text-lg text-white max-w-6xl mx-auto leading-relaxed drop-shadow-md px-4 pb-4">
              {t('home.description')}
            </p>
            
            <div className="flex flex-wrap gap-3 md:gap-4 justify-center items-center px-4">
              <div className="flex items-center gap-2 text-white bg-black/30 backdrop-blur-sm px-3 md:px-4 py-2 rounded-lg text-sm md:text-base">
                <Bed className="w-4 h-4 md:w-5 md:h-5 text-amber-400" />
                <span>{t('home.bedrooms')}</span>
              </div>
              <div className="flex items-center gap-2 text-white bg-black/30 backdrop-blur-sm px-3 md:px-4 py-2 rounded-lg text-sm md:text-base">
                <Users className="w-4 h-4 md:w-5 md:h-5 text-amber-400" />
                <span>{t('home.guests')}</span>
              </div>
              <div className="flex items-center gap-2 text-white bg-black/30 backdrop-blur-sm px-3 md:px-4 py-2 rounded-lg text-sm md:text-base">
                <Wifi className="w-4 h-4 md:w-5 md:h-5 text-amber-400" />
                <span>{t('home.wifi')}</span>
              </div>
              <div className="flex items-center gap-2 text-white bg-black/30 backdrop-blur-sm px-3 md:px-4 py-2 rounded-lg text-sm md:text-base">
                <Droplets className="w-4 h-4 md:w-5 md:h-5 text-amber-400" />
                <span>{t('home.pool')}</span>
              </div>
              <div className="flex items-center gap-2 text-white bg-black/30 backdrop-blur-sm px-3 md:px-4 py-2 rounded-lg text-sm md:text-base">
                <Waves className="w-4 h-4 md:w-5 md:h-5 text-amber-400" />
                <span>{t('home.seaAccess')}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Button after Hero */}
      <div className="bg-gradient-to-b from-black/40 to-white py-8 text-center">
        <Button
          data-testid="hero-inquiry-btn"
          onClick={handleInquiry}
          size="lg"
          className="bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white px-8 py-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
        >
          {t('home.inquireNow')}
        </Button>
      </div>

      {/* Image Gallery Carousel */}
      <section className="py-16 px-4 bg-gradient-to-b from-white to-amber-50">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-4">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('home.villaTitle')}</h2>
            <p className="text-lg text-gray-600 max-w-4xl mx-auto">
              {t('home.villaLocation')}
            </p>
          </div>
          
          {/* Google Maps */}
          <div className="max-w-4xl mx-auto mb-8">
            <Card className="overflow-hidden shadow-xl border-0 rounded-2xl">
              <div className="relative w-full h-[250px]">
                <iframe
                  title="Ferienhaus Standort"
                  src="https://maps.google.com/maps?q=12.336495,99.950359&hl=de&z=6&t=k&output=embed"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="rounded-2xl"
                ></iframe>
              </div>
            </Card>
          </div>
          
          <div className="text-center mb-4">
            <p className="text-lg text-gray-600 max-w-4xl mx-auto">
              {t('home.villaRegion')}
            </p>
            <p className="text-lg text-gray-600 mt-4">
              {t('home.villaView')}
            </p>
          </div>
          <ImageCarousel />
          
          {/* CTA Button after Gallery */}
          <div className="text-center mt-8">
            <Button
              data-testid="gallery-inquiry-btn"
              onClick={handleInquiry}
              size="lg"
              className="bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white px-8 py-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
            >
              {t('home.inquireNow')}
            </Button>
          </div>
        </div>
      </section>

      {/* Description Section */}
      <section id="villa" className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-4xl">
          <div className="space-y-8">
            <div className="text-center">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">{t('home.retreatTitle')}</h2>
            </div>
            
            <div className="prose prose-lg max-w-none text-gray-700 space-y-6">
              <p className="leading-relaxed">
                {t('home.retreatDescription')}
              </p>
              
              <div className="grid md:grid-cols-2 gap-6 my-8">
                <Card className="p-6 border-amber-200 hover:shadow-lg transition-shadow duration-300">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('home.quickInfo')}</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• {t('villa.features.plot')} / {t('villa.features.livingSpace')}</li>
                    <li>• {t('villa.features.bedrooms')} ({t('villa.features.guests')})</li>
                    <li>• {t('villa.features.bathrooms')}</li>
                    <li>• {t('villa.features.wifi')}</li>
                    <li>• {t('villa.features.kitchen')}</li>
                    <li>• {t('villa.features.pool')}</li>
                    <li>• {t('villa.features.seaAccess')}</li>
                    <li>• {t('villa.features.washer')}</li>
                    <li>• {t('villa.features.linens')}</li>
                    <li>• {t('villa.features.aircon')}</li>
                    <li>• {t('villa.features.parking')}</li>
                    <li>• {t('villa.features.noPets')}</li>
                  </ul>
                </Card>
                
                <Card className="p-6 border-amber-200 hover:shadow-lg transition-shadow duration-300">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('home.surroundings')}</h3>
                  <ul className="space-y-4 text-gray-700">
                    <li>
                      <strong>{t('home.surroundingsHuaHin')}</strong><br />
                      {t('home.surroundingsHuaHinDesc')}
                    </li>
                    <li>
                      <strong>{t('home.surroundingsBeaches')}</strong><br />
                      {t('home.surroundingsBeachesDesc')}
                    </li>
                  </ul>
                </Card>
              </div>
            </div>
            
            <div className="text-center pt-8">
              <Button 
                onClick={handleInquiry}
                size="lg"
                className="bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white px-8 py-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
              >
                {t('home.inquireNow')}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            {/* Social Media Links */}
            <div>
              <h3 className="text-xl font-semibold mb-4 text-amber-400">{t('footer.followUs')}</h3>
              <div className="flex gap-4">
                <a 
                  href="https://www.instagram.com/villa_thailand/" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-gray-400 hover:text-amber-400 transition-colors duration-300"
                >
                  <Instagram className="w-6 h-6" />
                  <span>Instagram</span>
                </a>
              </div>
              <div className="flex gap-4 mt-3">
                <a 
                  href="https://www.facebook.com/" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-gray-400 hover:text-amber-400 transition-colors duration-300"
                >
                  <Facebook className="w-6 h-6" />
                  <span>Facebook</span>
                </a>
              </div>
            </div>

            {/* Contact Link */}
            <div>
              <h3 className="text-xl font-semibold mb-4 text-amber-400">{t('footer.contact')}</h3>
              <Link 
                to={routes.contact} 
                className="flex items-center gap-2 text-gray-400 hover:text-amber-400 transition-colors duration-300"
              >
                <Mail className="w-6 h-6" />
                <span>{t('footer.contactUs')}</span>
              </Link>
            </div>

            {/* Legal Links */}
            <div>
              <h3 className="text-xl font-semibold mb-4 text-amber-400">{t('footer.legal')}</h3>
              <div className="flex flex-col gap-3">
                <Link 
                  to={routes.privacy} 
                  className="text-gray-400 hover:text-amber-400 transition-colors duration-300"
                >
                  {t('footer.privacy')}
                </Link>
                <Link 
                  to={routes.imprint} 
                  className="text-gray-400 hover:text-amber-400 transition-colors duration-300"
                >
                  {t('footer.imprint')}
                </Link>
              </div>
            </div>
          </div>

          {/* Copyright */}
          <div className="border-t border-gray-700 pt-6 text-center">
            <p className="text-gray-400">
              {t('footer.copyright')}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
